/**
 * Chat Loading Placeholder Component
 * Shows a placeholder loading state in the chat screen when waiting for response
 */

import React, { useState, useEffect } from 'react';
import {
  spacing,
  typographyColors,
  typography,
  fontPrimary,
  strokeColors,
  backgroundColors,
  borders,
} from '@/design-system/utils';
import { motion } from 'framer-motion';

export interface ChatLoadingPlaceholderProps {
  loadingPhase?: 'thinking' | 'collecting' | 'structuring';
}

export const ChatLoadingPlaceholder: React.FC<ChatLoadingPlaceholderProps> = ({
  loadingPhase = 'thinking',
}) => {
  const [currentPhase, setCurrentPhase] = useState(loadingPhase);

  useEffect(() => {
    // Always sync if prop changes to something else
    setCurrentPhase(loadingPhase);
  }, [loadingPhase]);

  useEffect(() => {
    // Only auto-sequence if we are in the initial 'thinking' phase
    // This provides a sense of progress even if Step 1 takes time
    if (loadingPhase === 'thinking') {
      const gatheringTimer = setTimeout(() => {
        setCurrentPhase('collecting');
      }, 2500);

      const structuringTimer = setTimeout(() => {
        setCurrentPhase('structuring');
      }, 5000);

      return () => {
        clearTimeout(gatheringTimer);
        clearTimeout(structuringTimer);
      };
    }
  }, [loadingPhase]);

  const getLoadingText = () => {
    switch (currentPhase) {
      case 'thinking':
        return 'Getting response...';
      case 'collecting':
        return 'Gathering information...';
      case 'structuring':
        return 'Structuring data...';
      default:
        return 'Getting response...';
    }
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
      }}
    >
      {/* Animated loading dots - Thinking indicator at the top */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing[2],
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
          }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: typographyColors.secondary,
              }}
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.2,
              }}
            />
          ))}
        </div>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
            margin: 0,
          }}
        >
          {getLoadingText()}
        </p>
      </div>

      {/* Overview Section Skeleton */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        {/* Title skeleton */}
        <div
          style={{
            width: '100%',
            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
        {/* Description skeleton */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
          }}
        >
          <div
            style={{
              width: '100%',
              height: '16px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
          <div
            style={{
              width: '100%',
              height: '16px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
          <div
            style={{
              width: '100%',
              height: '16px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          width: '100%',
          height: '1px',
          backgroundColor: strokeColors.base,
        }}
      />

      {/* Places of Relevance Section Skeleton */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        {/* Title skeleton */}
        <div
          style={{
            width: '100%',

            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
        {/* Horizontal scrollable cards skeleton */}
        <div
          style={{
            display: 'flex',
            gap: spacing[3],
            overflowX: 'hidden',
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              style={{
                minWidth: '170px',
                width: '170px',
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[2],
                backgroundColor: backgroundColors.primary,
                borderRadius: borders.radius.lg,
                padding: spacing[3],
                border: `1px solid ${strokeColors.base}`,
              }}
            >
              {/* Image skeleton */}
              <div
                style={{
                  width: '100%',
                  height: '140px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: borders.radius.md,
                }}
                className='animate-pulse'
              />
              {/* Title skeleton */}
              <div
                style={{
                  width: '100%',
                  height: '20px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
              {/* Location skeleton */}
              <div
                style={{
                  width: '100%',
                  height: '16px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          width: '100%',
          height: '1px',
          backgroundColor: strokeColors.base,
        }}
      />

      {/* Response Chat Skeleton */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
          width: '100%',
        }}
      >
        {/* Skeleton lines for message content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
            width: '100%',
            marginTop: spacing[2],
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              style={{
                width: '100%',
                height: '16px',
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
          ))}
        </div>
      </div>

      {/* People Also Ask Skeleton */}
      <div
        style={{
          width: '100%',
          backgroundColor: backgroundColors.secondary,
          borderRadius: borders.radius.lg,
          padding: spacing[3],
          marginTop: spacing[4],
        }}
      >
        {/* Header skeleton */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: spacing[2],
          }}
        >
          <div
            style={{
              width: '100%',
              // maxWidth: '150px',
              height: '20px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
          {/* <div
            style={{
              width: '100%',
              height: '20px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          /> */}
        </div>
        {/* Questions skeleton */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[0],
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index}>
              <div
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                  marginBottom: index < 2 ? spacing[0] : 0,
                }}
                className='animate-pulse'
              />
              {index < 2 && (
                <div
                  style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: backgroundColors.shade,
                    marginTop: spacing[0],
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
